import { inject, Injectable } from '@angular/core';
import { Dictionary } from '@ngrx/entity';

import { CanvasElement, CanvasElementId } from '@blaze-runtime/engine-domain';
import { FormListErrors, FormValidationErrors } from '@blaze-shared/elements-domain';
import { NotificationService, NotificationType } from '@blaze-shared/platform-data-access';
import { SchemaService } from '@blaze-shared/schema-data-access';

import { describeContainerErrors } from './form-validation-messages';

/**
 * Reports rejected form submissions to the user: turns a container's errors into the text
 * that names the offending field, and shows it.
 */
@Injectable({ providedIn: 'root' })
export class FormValidationReportService {
  private readonly notificationService = inject(NotificationService);
  private readonly schemaService = inject(SchemaService);

  /** Describes a container's errors as the HTML shown when its commit is rejected. */
  describeErrors(
    containerId: CanvasElementId,
    containerType: 'formContainer' | 'editableList' | 'formDetails',
    errors: Dictionary<FormValidationErrors> | Dictionary<FormListErrors> | undefined,
    elements: Dictionary<CanvasElement>,
  ): string {
    return describeContainerErrors(containerId, containerType, errors, elements, (column) =>
      this.schemaService.getColumnNameLatest(column),
    );
  }

  /** Shows described errors as an error notification. */
  showErrors(errorsHtml: string): void {
    const notification = this.notificationService.createNotification({
      type: NotificationType.Error,
      applyTheme: true,
      message: '',
      messageHtml: errorsHtml,
    });
    this.notificationService.showNotification(notification);
  }
}
